import React, { useState } from "react";
import { AssistantTextWithPathCards } from "./WorkspaceFileLink";

/**
 * CCAgentTurnCard —— 一次"研讨"（主智能体调用某个子智能体）的整体卡片。
 *
 * 认知协同界面的核心：把子智能体（导师/专家）的内部输出从主智能体正文里独立出来，
 * 清楚呈现"是谁在发言、这是哪一次研讨、学生可以做什么"。
 *
 * props.turn = {
 *   toolId, agentName, agentDesc, promptText,
 *   reply, streamingReply, innerTools:[{toolName,toolInput,result,isError}],
 *   status: "running" | "done"
 * }
 * props.roster    = [{ name, description, role }]   用于补全角色描述与分类图标
 * props.onInsertPrompt(text)                        把快捷追问填入输入框（学生确认后再发）
 */

// 角色分类 → 图标 / 主题色（role 为可选字段，缺省用通用样式）
const ROLE_META = {
  mentor:      { icon: "🧭", color: "#d29922", label: "导师" },
  expert:      { icon: "🔬", color: "#58a6ff", label: "专家" },
  stakeholder: { icon: "🧑‍💼", color: "#a371f7", label: "干系人" },
  compliance:  { icon: "🛡️", color: "#f0883e", label: "合规" },
};
const DEFAULT_META = { icon: "🤝", color: "#3fb950", label: "研讨角色" };

const metaFor = (role) => ROLE_META[role] || DEFAULT_META;

// 把 subagent_type（如 perception-expert）转成更友好的展示名
const prettyName = (name) => {
  if (!name) return "研讨角色";
  return name
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const FILE_TOOLS = new Set(["Read", "Write", "Edit", "MultiEdit"]);

const InnerToolList = ({ tools, onOpenFile }) => {
  const [open, setOpen] = useState(false);
  const [hints, setHints] = useState({});
  if (!tools || tools.length === 0) return null;
  return (
    <div style={{ marginTop: "6px" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "none",
          border: "1px solid #30363d",
          borderRadius: "12px",
          color: "#8b949e",
          fontSize: "11px",
          padding: "2px 10px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {open ? "▲ 收起" : "▼ 展开"}该角色的 {tools.length} 步内部操作
      </button>
      {open && (
        <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "3px" }}>
          {tools.map((t, i) => (
            <div
              key={t.toolId || i}
              style={{
                fontSize: "11px",
                color: t.isError ? "#f85149" : "#8b949e",
                padding: "3px 8px",
                background: "#0d1117",
                border: "1px solid #21262d",
                borderRadius: "4px",
                fontFamily: "'Courier New', monospace",
                wordBreak: "break-all",
              }}
            >
              <span style={{ color: t.isError ? "#f85149" : "#6e9bd1" }}>
                {t.isError ? "✗" : "•"} {t.toolName || "工具"}
              </span>
              {t.toolInput && Object.keys(t.toolInput).length > 0 && (
                <span style={{ color: "#484f58" }}>
                  {"  "}
                  {(() => {
                    const s = JSON.stringify(t.toolInput);
                    return s.length > 120 ? s.slice(0, 120) + "…" : s;
                  })()}
                </span>
              )}
              {onOpenFile && t.toolInput?.file_path && FILE_TOOLS.has(t.toolName) && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const key = t.toolId || i;
                    setHints((prev) => ({ ...prev, [key]: "打开中…" }));
                    try {
                      const result = await onOpenFile(t.toolInput.file_path);
                      setHints((prev) => ({
                        ...prev,
                        [key]: result && result.ok === false ? (result.error || "打开失败") : "",
                      }));
                    } catch (err) {
                      setHints((prev) => ({ ...prev, [key]: err.message || "打开失败" }));
                    }
                  }}
                  style={{
                    marginLeft: 8,
                    border: "1px solid #30363d",
                    background: "#161b22",
                    color: hints[t.toolId || i] && hints[t.toolId || i] !== "打开中…" ? "#f85149" : "#58a6ff",
                    borderRadius: 4,
                    fontSize: 10,
                    padding: "0 6px",
                    cursor: "pointer",
                  }}
                >
                  {hints[t.toolId || i] && hints[t.toolId || i] !== "打开中…" ? "失败" : "打开"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuickActions = ({ displayName, onInsertPrompt }) => {
  if (!onInsertPrompt) return null;
  const actions = [
    { label: `↩ 追问${displayName}`, text: `请${displayName}就刚才的观点进一步展开说明：` },
    { label: "🔀 换个专家看看", text: "我想听听其他专家对这个问题的不同看法。" },
    { label: "📌 记入方案", text: `请把${displayName}刚才的关键结论整理并写入当前方案文档。` },
  ];
  return (
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
      {actions.map((a, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onInsertPrompt(a.text)}
          style={{
            background: "#161b22",
            border: "1px solid #30363d",
            borderRadius: "14px",
            padding: "3px 12px",
            fontSize: "12px",
            color: "#9cc9ff",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
};

const CCAgentTurnCard = ({ turn, roster, onInsertPrompt, onOpenFile }) => {
  if (!turn) return null;

  const rosterItem = (roster || []).find((r) => r.name === turn.agentName);
  const role = rosterItem?.role || "";
  const meta = metaFor(role);
  const displayName = prettyName(turn.agentName);
  const description = turn.agentDesc || rosterItem?.description || "";
  const running = turn.status !== "done";
  const spokenText = [turn.reply, turn.streamingReply].filter(Boolean).join("\n");

  return (
    <div
      style={{
        margin: "8px 0",
        border: `1px solid ${meta.color}55`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: "10px",
        background: `linear-gradient(160deg, ${meta.color}12 0%, rgba(13,17,23,0.96) 60%)`,
        overflow: "hidden",
      }}
    >
      {/* 头部：认知协同标识 + 角色身份 + 状态 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 12px",
          background: `${meta.color}18`,
          borderBottom: `1px solid ${meta.color}33`,
        }}
      >
        <span style={{ fontSize: "16px", flexShrink: 0 }}>{meta.icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ color: "#e6edf3", fontSize: "13px", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayName}
            </span>
            <span
              style={{
                flexShrink: 0,
                fontSize: "10px",
                color: meta.color,
                border: `1px solid ${meta.color}66`,
                borderRadius: "3px",
                padding: "0 5px",
                lineHeight: "15px",
              }}
            >
              {meta.label}
            </span>
          </div>
          <div style={{ fontSize: "10px", color: "#8b949e", marginTop: "1px" }}>
            认知协同 · 研讨回合
          </div>
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: "11px",
            color: running ? "#f0883e" : "#3fb950",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              background: running ? "#f0883e" : "#3fb950",
              boxShadow: running ? "0 0 6px #f0883e" : "none",
              display: "inline-block",
            }}
          />
          {running ? "研讨中" : "已完成"}
        </span>
      </div>

      {/* 正文 */}
      <div style={{ padding: "8px 12px" }}>
        {description && (
          <div
            style={{
              fontSize: "11px",
              color: "#8b949e",
              marginBottom: "6px",
              paddingLeft: "8px",
              borderLeft: "2px solid #30363d",
            }}
          >
            任务：{description}
          </div>
        )}

        {spokenText ? (
          <div style={{ fontSize: "13px", color: "#e6edf3", lineHeight: 1.6 }}>
            <AssistantTextWithPathCards content={spokenText} onOpen={onOpenFile} disableMermaid={true} />
            {running && turn.streamingReply && (
              <span
                style={{
                  display: "inline-block",
                  width: "2px",
                  height: "14px",
                  background: meta.color,
                  marginLeft: "2px",
                  animation: "cc-blink 1s step-end infinite",
                  verticalAlign: "text-bottom",
                }}
              />
            )}
          </div>
        ) : (
          <div style={{ fontSize: "12px", color: "#6e7681", fontStyle: "italic" }}>
            {running ? `${displayName}正在思考…` : "（无文字回复）"}
          </div>
        )}

        <InnerToolList tools={turn.innerTools} onOpenFile={onOpenFile} />

        {!running && <QuickActions displayName={displayName} onInsertPrompt={onInsertPrompt} />}
      </div>
    </div>
  );
};

export default CCAgentTurnCard;
